/**
 * Human-readable labels for the uC numeric state enums (spec §8.5 +
 * §9.3 runtime). Keep this in lockstep with uc-gateway-protocol-spec.md
 * — when firmware adds a state or error code, edit this file.
 */

export const SYS_STATE_LABEL: Record<number, string> = {
  0: 'Standby',
  1: 'Warming',
  2: 'LampOn',
  3: 'Cooling',
  4: 'Zoom',
  5: 'Pause',
  6: 'Freeze',
  7: 'AudioOnly',
  8: 'AudioOnlyCooling',
};

export const ERR_STATE_LABEL: Record<number, string> = {
  [-1]: 'NoErrorInfo',
  0: 'NoError',
  1: 'FanWarning',
  4: 'TempError',
  6: 'LaserError',
  0xfe: 'OtherError',
};

export const DEMO_MODE_LABEL: Record<number, string> = {
  0: 'unsupported',
  1: 'supported',
  2: 'active',
  3: 'suspended',
};

// Projector input sources — spec §8.3 set_source command table.
export const PROJ_SOURCE_LABEL: Record<number, string> = {
  0x30: 'HDMI1',
  0xa0: 'HDMI2',
  0xc0: 'HDMI3',
  0x41: 'Video RCA',
  0x80: 'HDBaseT',
  0x51: 'USB Display',
  0x52: 'USB1',
  0x53: 'LAN',
  0x56: 'Screen Mirroring',
  0x10: 'Computer1',
};

// Canonical projector-source identity for the System status timeline. MUST
// mirror the aggregator's SOURCE_LABELS
// (greengrass-components/artifacts/usage-aggregator.py) byte-for-byte —
// including the `src_0xNN` fallback — because the timeline dedupes a source by
// this string and the aggregator is what actually wrote the `source_dwell`
// event labels. Kept separate from PROJ_SOURCE_LABEL (the richer spec §8.3
// naming used for the numeric shadow-doc display); see canonSource().
export const SOURCE_CANON: Record<number, string> = {
  0x00: 'Off',
  0x30: 'HDMI1',
  0xa0: 'HDMI2',
  0xc0: 'HDMI3',
  0x41: 'RCA',
  0x80: 'HDBaseT',
  0x51: 'USBDisplay',
};

// Canonicalise a projector source (numeric code or string label) to the single
// string identity used by the timeline. Numeric → SOURCE_CANON or the
// aggregator's `src_0xNN` fallback (uppercase hex, ≥2 digits); non-empty string
// passes through; null / empty / invalid → null.
export function canonSource(v: number | string | null | undefined): string | null {
  if (v === null || v === undefined) return null;
  if (typeof v === 'string') return v.length > 0 ? v : null;
  if (!Number.isFinite(v)) return null;
  return SOURCE_CANON[v] ?? `src_0x${v.toString(16).toUpperCase().padStart(2, '0')}`;
}

function fmtNum(n: number | null | undefined, table: Record<number, string>): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  const label = table[n];
  return label !== undefined ? `${label} (${n})` : String(n);
}

export const fmtSysState = (n: number | null | undefined) => fmtNum(n, SYS_STATE_LABEL);
export const fmtErrState = (n: number | null | undefined) => fmtNum(n, ERR_STATE_LABEL);
export const fmtDemoMode = (n: number | null | undefined) => fmtNum(n, DEMO_MODE_LABEL);
export const fmtProjSource = (n: number | null | undefined) => fmtNum(n, PROJ_SOURCE_LABEL);
